import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { AuthenticationService } from './services/authentication.service';
import { AuthorizationService } from './services/authorization.service';

const TOKEN_REFRESH_TIME_BUFFER = 60000; // 1 minute

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
  ) {}

  // Not sure if there is a better way of doing this. The
  // /api/v3/offers and
  // /api/v3/azure-market-place
  // Urls end points are public we have to get the data passed in Ids or tokens
  // to work out how to login and where to go
  private isServicesOfferApi(requestUrl: string): boolean {
    return (
      requestUrl.indexOf(`${environment.servicesApiUrl}/api/v3/offers/`) === 0 ||
      requestUrl.indexOf(`${environment.servicesApiUrl}/api/v3/azure-market-place`) === 0
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isServicesApi = request.url.indexOf(environment.servicesApiUrl) === 0;
    if (!isServicesApi) {
      return next.handle(request);
    }

    const isServicesOfferApi = this.isServicesOfferApi(request.url);
    if (isServicesOfferApi) {
      return next.handle(request);
    }
    // first check the access token, and refresh if necessary
    return this.checkAuthTokenExpiry().pipe(
      switchMap(() => {
        // Then make the request with a valid access token
        return next.handle(this.addAuthHeader(request));
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.handleUnauthorized(err, next, request);
        }
        return throwError(err);
      }),
      tap({
        error: err => {
          if (err instanceof HttpErrorResponse && err.status === 403) {
            this.onForbidden();
          }
        },
      }),
    );
  }

  private checkAuthTokenExpiry(): Observable<any> {
    const tokenExpirationTime = this.authenticationService.getAccessTokenExpiration();
    if (tokenExpirationTime && this.shouldRefresh(tokenExpirationTime)) {
      // Token has expired, or is about to expire, refresh it
      return from(this.authenticationService.silentRefresh());
    } else {
      // Token is still valid, return the "empty" emmitting observable. This must be of({}) as otherwise next is not emitted
      return of({});
    }
  }

  private shouldRefresh(tokenExpirationTime: number): boolean {
    const currentTime = new Date().getTime();
    const refreshAtTime = tokenExpirationTime - TOKEN_REFRESH_TIME_BUFFER;
    return currentTime > refreshAtTime;
  }

  private addAuthHeader(request: HttpRequest<any>) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.authenticationService.getAccessToken()}`,
      },
    });
  }

  private handleUnauthorized(err: HttpErrorResponse, next: HttpHandler, request: HttpRequest<any>) {
    return from(this.authenticationService.silentRefresh()).pipe(
      switchMap(() => {
        if (this.authenticationService.isAuthenticated()) {
          // Need to use new access token
          return next.handle(this.addAuthHeader(request));
        }

        return throwError(err);
      }),
    );
  }

  private onForbidden() {
    this.authorizationService.refreshPermissions();
  }
}
